import type { ReactNode } from 'react';
import { fa, toman } from './fa';

/*
 * The three overlays the sale screen opens.
 *
 * Modal rather than a side sheet, and inside the device frame rather than over
 * the whole page — the design is explicit, and it matters on a till: the
 * cashier's hands are on a 1280px tablet, and something that slides in from an
 * edge is something they have to reach across the counter for.
 */

function Frame({
  title, subtitle, onClose, children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, background: 'rgba(32,30,29,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, zIndex: 10,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560, maxHeight: '100%', background: 'var(--luma-card)', borderRadius: 12,
          boxShadow: '0 12px 32px rgba(32,30,29,.32)', display: 'flex',
          flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 18px', borderBottom: '1px solid var(--pos-rule)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 19 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 44, height: 44, borderRadius: 10,
              border: '1px solid var(--luma-line-strong)', fontSize: 15,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export interface ParkedSale {
  id: string;
  who: string;
  lineCount: number;
  totalRial: number;
  parkedAt: string;
}

export function ParkModal({
  parked, onRetrieve, onClose,
}: {
  parked: ParkedSale[];
  onRetrieve: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <Frame title="فروش‌های پارک‌شده" subtitle="سبد را برگردانید و از همان‌جا ادامه دهید." onClose={onClose}>
      <div style={{ padding: '8px 0', overflow: 'auto' }}>
        {parked.length === 0 && (
          <div style={{ padding: '34px 18px', textAlign: 'center', color: 'var(--luma-muted)', fontSize: 13.5 }}>
            هیچ فروشی پارک نشده است.
          </div>
        )}
        {parked.map((k) => (
          <button
            key={k.id}
            onClick={() => onRetrieve(k.id)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 18px', textAlign: 'right', minHeight: 64,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{k.who}</div>
              <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>
                {fa(k.lineCount)} قلم · پارک‌شده {k.parkedAt}
              </div>
            </div>
            <div className="pos-num" style={{ fontWeight: 700, fontSize: 15 }}>{toman(k.totalRial)}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-accent-hover)' }}>بازگرداندن</div>
          </button>
        ))}
      </div>
    </Frame>
  );
}

export function CustomerModal({
  draft, onDraft, onAttach, onClose,
}: {
  draft: string;
  onDraft: (v: string) => void;
  onAttach: () => void;
  onClose: () => void;
}) {
  // Ten digits without the leading zero, eleven with it — both are how people
  // actually give a number, so both are accepted.
  const ready = draft.length >= 10;

  return (
    <Frame
      title="مشتری فاکتور"
      subtitle="شماره موبایل برای فاکتور الکترونیکی."
      onClose={onClose}
    >
      <div style={{ padding: 18, display: 'flex', gap: 18 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            className="pos-num"
            style={{
              height: 60, border: '1px solid var(--luma-line-strong)', borderRadius: 12,
              background: 'var(--luma-sunken)', display: 'flex', alignItems: 'center',
              padding: '0 16px', fontWeight: 700, fontSize: 22, letterSpacing: '.04em',
              boxShadow: '0 0 0 4px rgba(249,124,21,.10)',
            }}
          >
            {draft ? fa(draft) : ''}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)', lineHeight: 1.65 }}>
            شماره فقط روی همین فاکتور ذخیره می‌شود تا فاکتور الکترونیکی برای مشتری ارسال شود.
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onAttach}
            disabled={!ready}
            style={{
              height: 56, borderRadius: 12, fontWeight: 800, fontSize: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px',
              background: ready ? 'var(--luma-accent)' : 'var(--luma-line)',
              color: ready ? '#FFF' : 'var(--pos-disabled-ink)',
            }}
          >
            <span>افزودن به فاکتور</span>
            <span style={{ fontSize: 18 }}>←</span>
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, width: 238 }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clr', '0', 'del'].map((k) => (
            <button
              key={k}
              onClick={() =>
                onDraft(
                  k === 'del' ? draft.slice(0, -1)
                  : k === 'clr' ? ''
                  : (draft + k).slice(0, 11),
                )
              }
              style={{
                height: 56, borderRadius: 12, border: '1px solid var(--luma-line)',
                background: 'var(--luma-sunken)', fontFamily: 'var(--luma-font-money)',
                fontWeight: 700, fontSize: 19,
              }}
            >
              {k === 'del' ? '⌫' : k === 'clr' ? 'پاک' : fa(k)}
            </button>
          ))}
        </div>
      </div>
    </Frame>
  );
}

export function DiscountModal({
  subtotalRial, currentRial, canApprove, onPick, onClear, onClose,
}: {
  subtotalRial: number;
  currentRial: number;
  canApprove: boolean;
  onPick: (rial: number) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  return (
    <Frame
      title="تخفیف فاکتور"
      subtitle={
        currentRial > 0
          ? `تخفیف فعلی فاکتور: ${toman(currentRial)}`
          : 'روی این فاکتور هنوز تخفیفی اعمال نشده است.'
      }
      onClose={onClose}
    >
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)' }}>درصدی</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[5, 10, 15, 20].map((p) => (
            <button
              key={p}
              onClick={() => onPick(Math.round((subtotalRial * p) / 100))}
              style={{
                flex: 1, height: 56, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
                background: 'var(--luma-card)', fontWeight: 800, fontSize: 17,
              }}
            >
              {fa(p)}٪
            </button>
          ))}
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)' }}>مبلغ ثابت</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[50_000, 100_000, 200_000].map((t) => (
            <button
              key={t}
              onClick={() => onPick(t * 10)}
              style={{
                flex: 1, height: 56, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
                background: 'var(--luma-card)', fontWeight: 700, fontSize: 15,
              }}
            >
              {fa(t.toLocaleString('en-US').replace(/,/g, '٬'))}
            </button>
          ))}
        </div>

        {/*
          Said before the discount is picked, not after it is refused.
          The cashier who cannot approve their own 25% needs to know that while
          the customer is still standing there, not once the sale bounces.
        */}
        <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)', lineHeight: 1.65 }}>
          {canApprove
            ? 'هر تخفیف با نام شما و مبلغ آن در گزارش ممیزی ثبت می‌شود.'
            : 'شما دسترسی ثبت تخفیف ندارید — برای اعمال تخفیف از سرشیفت بخواهید.'}
        </div>

        <button
          onClick={onClear}
          style={{
            height: 48, borderRadius: 10, border: '1px solid var(--pos-danger-line)',
            background: 'var(--luma-card)', color: 'var(--luma-danger)', fontWeight: 700, fontSize: 14,
          }}
        >
          برداشتن تخفیف
        </button>
      </div>
    </Frame>
  );
}

/*
 * Typing a weight.
 *
 * A connected scale reads through the local agent's serial port, which is not
 * wired up yet — so rather than a button that pretends, the cashier reads the
 * number off the scale's own display and types it. That is what shops without
 * an integrated scale do today anyway, and it makes موز sellable at 1.24kg
 * instead of being rounded to whole kilos.
 *
 * The decimal mark is «٫» throughout: ۱.۲۴ reads as two numbers to a Persian
 * speaker, and a mis-read weight is a mis-charged customer.
 */
export function QuantityModal({
  itemName, unitPriceRial, draft, onDraft, onConfirm, onClose,
}: {
  itemName: string;
  unitPriceRial: number;
  draft: string;
  onDraft: (v: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const value = Number(draft.replace('٫', '.')) || 0;
  const ready = value > 0;

  return (
    <Frame title="وزن یا تعداد" subtitle={itemName} onClose={onClose}>
      <div style={{ padding: 18, display: 'flex', gap: 18 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            className="pos-num"
            style={{
              height: 74, border: '1px solid var(--luma-line-strong)', borderRadius: 12,
              background: 'var(--luma-sunken)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontWeight: 800, fontSize: 38,
              boxShadow: '0 0 0 4px rgba(249,124,21,.10)',
            }}
          >
            {draft ? fa(draft) : '۰'}
          </div>
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              borderTop: '1px solid var(--pos-rule)', paddingTop: 12,
            }}
          >
            <span style={{ fontSize: 13.5, color: 'var(--luma-ink-2)', fontWeight: 600 }}>مبلغ ردیف</span>
            <span className="pos-num" style={{ fontWeight: 800, fontSize: 26 }}>
              {toman(Math.round(unitPriceRial * value))}
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onConfirm}
            disabled={!ready}
            style={{
              height: 56, borderRadius: 12, fontWeight: 800, fontSize: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px',
              background: ready ? 'var(--luma-accent)' : 'var(--luma-line)',
              color: ready ? '#FFF' : 'var(--pos-disabled-ink)',
            }}
          >
            <span>افزودن به فاکتور</span>
            <span style={{ fontSize: 18 }}>+</span>
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, width: 238 }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '٫', '0', 'del'].map((k) => (
            <button
              key={k}
              onClick={() =>
                onDraft(
                  k === 'del' ? draft.slice(0, -1)
                  : k === '٫' ? (draft.includes('٫') || draft === '' ? draft : draft + '٫')
                  : (draft + k).slice(0, 7),
                )
              }
              style={{
                height: 56, borderRadius: 12, border: '1px solid var(--luma-line)',
                background: 'var(--luma-sunken)', fontFamily: 'var(--luma-font-money)',
                fontWeight: 700, fontSize: 19,
              }}
            >
              {k === 'del' ? '⌫' : k === '٫' ? '٫' : fa(k)}
            </button>
          ))}
        </div>
      </div>
    </Frame>
  );
}

/*
 * An order arrived while the cashier was doing something else.
 *
 * The arrivals screen is a list you have to go and look at, which is the wrong
 * shape for the thing it holds: a kiosk order is a customer already paid and
 * already waiting, and a table round is a kitchen that has not been told. Both
 * are time-critical and neither announces itself. A counter at lunch does not
 * check a tab.
 *
 * Dismissing it is not the same as dealing with it — the order stays in the
 * list and the badge keeps counting. This only makes sure nobody can say they
 * were never told.
 */
export function NewArrivalModal({
  arrivals, onOpenList, onClose,
}: {
  arrivals: { id: string; kind?: string; tableLabel?: string; number: string; totalRial: number; paid?: boolean; lines: { name: string; quantity: number }[] }[];
  onOpenList: () => void;
  onClose: () => void;
}) {
  const many = arrivals.length > 1;
  const head = arrivals[0];
  if (!head) return null;

  return (
    <Frame
      title={many ? `${fa(arrivals.length)} سفارش تازه رسید` : 'سفارش تازه رسید'}
      subtitle={many ? 'همه در فهرست سفارش‌های ورودی هستند.' : undefined}
      onClose={onClose}
    >
      <div style={{ padding: '4px 0 8px', overflow: 'auto' }}>
        {arrivals.map((a) => (
          <div
            key={a.id}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '14px 18px', borderBottom: '1px solid var(--luma-line)',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                {a.kind === 'table' && a.tableLabel ? a.tableLabel : fa(a.number)}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--luma-muted)', marginTop: 3, lineHeight: 1.7 }}>
                {a.lines.length > 0
                  ? a.lines.slice(0, 3).map((l) => `${fa(l.quantity)}× ${l.name}`).join('، ')
                  : 'بدون قلم'}
                {a.lines.length > 3 ? ` و ${fa(a.lines.length - 3)} قلم دیگر` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div className="pos-num" style={{ fontWeight: 700, fontSize: 15 }}>{toman(a.totalRial)}</div>
              {/*
                * Said only when it is true, and it is the difference between
                * handing food over and asking for money. A kiosk order is
                * already paid; a table round is guests still sitting there.
                */}
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3, color: a.paid ? 'var(--luma-success)' : 'var(--luma-muted)' }}>
                {a.paid ? 'پرداخت‌شده' : 'پرداخت‌نشده'}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, padding: '14px 18px' }}>
        <button
          onClick={onOpenList}
          style={{
            flex: 1, minHeight: 52, borderRadius: 12, fontWeight: 700, fontSize: 15,
            background: 'var(--luma-accent)', color: '#fff',
          }}
        >
          دیدن سفارش‌ها
        </button>
        <button
          onClick={onClose}
          style={{
            minWidth: 120, minHeight: 52, borderRadius: 12, fontWeight: 700, fontSize: 15,
            border: '1px solid var(--luma-line-strong)',
          }}
        >
          باشه
        </button>
      </div>
    </Frame>
  );
}

/*
 * A kitchen ticket the pass never got.
 *
 * The till retries a failed print by itself every fifteen seconds, so this is
 * not here to make printing work — it is here because a ticket that keeps
 * failing looks, on the آشپزخانه screen, exactly like one that printed. The
 * kitchen is not making the food and nobody at the counter knows. A table
 * waits for an order that was never cooked.
 *
 * So it is a modal, not a chip: this is the one thing on the till that cannot
 * be noticed later. It does not block the sale — it is dismissible, and the
 * retry continues underneath either way.
 */
export function TicketDidNotPrintModal({
  tickets, onRetry, onClose, busyId,
}: {
  tickets: { ticketId: string; number: string; error: string }[];
  onRetry: (ticketId: string) => void;
  onClose: () => void;
  busyId: string | null;
}) {
  const head = tickets[0];
  if (!head) return null;
  const many = tickets.length > 1;

  return (
    <Frame
      title={many ? `${fa(tickets.length)} سفارش چاپ نشد` : 'سفارش چاپ نشد'}
      subtitle="آشپزخانه این سفارش را روی کاغذ نگرفته — روی صفحهٔ آشپزخانه هست، اما ممکن است کسی آن را نپخته باشد."
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
        {tickets.map((t) => (
          <div
            key={t.ticketId}
            style={{
              border: '1px solid var(--luma-tint-danger-border)',
              background: 'var(--luma-tint-danger-bg)',
              borderRadius: 12, padding: 14,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--luma-tint-danger-text)' }}>
                {t.number ? `سفارش ${fa(t.number)}` : 'سفارش'}
              </div>
              <button
                onClick={() => onRetry(t.ticketId)}
                disabled={busyId === t.ticketId}
                style={{
                  height: 44, padding: '0 18px', borderRadius: 10, fontWeight: 700, fontSize: 14,
                  border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                  opacity: busyId === t.ticketId ? 0.6 : 1,
                }}
              >
                {busyId === t.ticketId ? 'در حال چاپ…' : 'تلاش دوباره'}
              </button>
            </div>
            {/*
              The printer's own words, not ours. "چاپگر جواب نداد" sends
              somebody to look at the printer; "چاپ نشد" sends them to ask us.
            */}
            {t.error && (
              <div style={{ fontSize: 12.5, color: 'var(--luma-muted)', lineHeight: 1.8, direction: 'ltr', textAlign: 'left' }}>
                {t.error}
              </div>
            )}
          </div>
        ))}
        <div style={{ fontSize: 12.5, color: 'var(--luma-muted)', lineHeight: 1.9 }}>
          صندوق خودش هر چند ثانیه دوباره تلاش می‌کند. اگر کاغذ تمام شده یا چاپگر خاموش است،
          درستش کنید و «تلاش دوباره» را بزنید.
        </div>
      </div>
    </Frame>
  );
}

/*
 * «این سبد را دوباره پرداخت کنم؟»
 *
 * The agent noticed an identical basket charged in the last few minutes under
 * a different sale. It is a question rather than a refusal because a cafe
 * sells the same espresso to a queue of people all morning — but the amount is
 * held back until somebody answers, because the alternative is what happened:
 * a customer charged 22,000 twice for one coffee, discovered from the kitchen
 * printer.
 *
 * Cancel is the resting state and the wider button. The dangerous answer is
 * the one that costs a real person real money, so it does not get to be the
 * one a thumb lands on.
 */
export function ChargeAgainModal({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(32,30,29,.45)' }}
        onClick={onCancel}
      />
      <div
        dir="rtl"
        style={{
          position: 'fixed', zIndex: 61, top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 'min(92vw, 430px)', background: 'var(--luma-card)', borderRadius: 16,
          border: '1px solid var(--luma-line-strong)', padding: 20, display: 'grid', gap: 14,
          boxShadow: '0 18px 50px rgba(0,0,0,.22)',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--luma-ink)' }}>
          این سبد چند لحظه پیش پرداخت شد
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.9, color: 'var(--luma-muted)' }}>
          {message}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 2, height: 48, borderRadius: 12, fontWeight: 800, fontSize: 15,
              border: 'none', background: 'var(--luma-accent)', color: '#FFF',
            }}
          >
            نه، پرداخت نکن
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 14,
              background: 'var(--luma-card)', color: 'var(--luma-muted)',
              border: '1px solid var(--luma-line-strong)',
            }}
          >
            مشتری دیگری است
          </button>
        </div>
      </div>
    </>
  );
}
